import { useMemo } from 'react';
import { coverageAreaZctas, averageScoresForCoverageArea, stateZctas } from '../utils/zctaScoring';

/** Derive all coverage-area scores from ZCTA data + work/home centers. */
export function useZctaScores({ workCenter, homeCenter, zctaCrimeData, zctaMedianRent, zctaLivabilityData, searchScope }) {
  const coverageZctas = useMemo(
    () => {
      if (searchScope?.mode === 'state' && searchScope?.state) {
        return stateZctas(searchScope.state, zctaCrimeData);
      }
      return coverageAreaZctas(workCenter, homeCenter, zctaCrimeData);
    },
    [workCenter, homeCenter, zctaCrimeData, searchScope]
  );

  const coverageAreaScores = useMemo(
    () => averageScoresForCoverageArea(coverageZctas, zctaCrimeData, zctaMedianRent, zctaLivabilityData),
    [coverageZctas, zctaCrimeData, zctaMedianRent, zctaLivabilityData]
  );

  // Both work and home use the same area-averaged scores
  const safetyScores = useMemo(() => ({
    work: coverageAreaScores.safety,
    home: coverageAreaScores.safety,
  }), [coverageAreaScores.safety]);

  const affordabilityScores = useMemo(() => ({
    work: coverageAreaScores.cost,
    home: coverageAreaScores.cost,
  }), [coverageAreaScores.cost]);

  const livabilityScores = useMemo(() => ({
    work: coverageAreaScores.livability,
    home: coverageAreaScores.livability,
    workBreakdown: coverageAreaScores.livabilityBreakdown,
    homeBreakdown: coverageAreaScores.livabilityBreakdown,
  }), [coverageAreaScores.livability, coverageAreaScores.livabilityBreakdown]);

  return { coverageAreaScores, safetyScores, affordabilityScores, livabilityScores };
}
